/**
 * A janela do relatório PDF do Financeiro: abre uma aba nova, escreve o documento e manda imprimir
 * depois de 600 ms, como o legado (`admin.html:8619` a `8626`). O "PDF" é o "Salvar como PDF" da
 * impressão do navegador, que é o que o legado faz.
 *
 * AS CORES VÊM DOS TOKENS, e não de literal: a regra da casa (ADR 0004) põe cor só em
 * `packages/tokens`. O relatório é um documento à parte, sem a folha de estilo do app, então a tela
 * lê os valores do TEMA CLARO na regra `:root` do `tokens.css` já carregado e os declara no topo do
 * documento como `--cx-*`. O tema claro sempre, mesmo com o painel no escuro, porque o relatório do
 * legado é impresso em papel branco.
 *
 * DIFERENÇA DECLARADA: o estilo do legado (`admin.html:8724`) usa sete tons. Três são tokens exatos
 * (o texto, o verde de sucesso e o vermelho de erro, os mesmos valores do legado segundo os
 * comentários de `tokens.css`); os outros não têm token e usam o mais próximo: o cinza do subtítulo
 * e do cabeçalho de coluna vira `texto-suave`, o cinza do rótulo e do "Pendente" vira `texto-fraco`,
 * a borda clara vira `borda`, a linha mais clara entre parcelas vira `borda` a 45%, e o fundo da
 * linha de grupo vira `superficie`. Registrado em PARIDADE-A5.
 */

// Os tokens que o relatório usa; a ordem não importa.
const TOKENS_DO_RELATORIO = ['texto', 'texto-suave', 'texto-fraco', 'borda', 'superficie', 'sucesso', 'erro'] as const;

/**
 * O bloco `:root{...}` com os valores do tema claro, lidos da regra `:root` das folhas de estilo da
 * página. Folha de outra origem recusa a leitura (`cssRules` lança), e é pulada.
 */
export function coresDoRelatorio(): string {
  const valores = new Map<string, string>();
  for (const folha of Array.from(document.styleSheets)) {
    let regras: CSSRuleList;
    try {
      regras = folha.cssRules;
    } catch {
      // Folha de outra origem: não há o que ler nela.
      continue;
    }
    for (const regra of Array.from(regras)) {
      // Só a regra `:root` pura, que é a do tema claro; a do escuro é `[data-theme='dark']`.
      if (!(regra instanceof CSSStyleRule) || regra.selectorText !== ':root') continue;
      for (const nome of TOKENS_DO_RELATORIO) {
        const valor = regra.style.getPropertyValue(`--cx-${nome}`).trim();
        // A primeira folha que declara vence, como a ordem de carga dos tokens.
        if (valor && !valores.has(nome)) valores.set(nome, valor);
      }
    }
  }
  const declaracoes = Array.from(valores, ([nome, valor]) => `--cx-${nome}:${valor};`).join('');
  return `:root{${declaracoes}}`;
}

/**
 * Abre a aba, escreve e imprime. Chamado DENTRO do clique, sem espera antes: navegador só deixa
 * abrir aba nova em resposta direta a um gesto, e o legado também chama sem espera antes.
 */
export function abrirRelatorio(html: string): void {
  const janela = window.open('', '_blank');
  // Bloqueador de janela: sem janela não há o que escrever, e a tela segue sem erro.
  if (!janela) return;
  janela.document.write(html);
  janela.document.close();
  // 600 ms: o tempo que o legado dá para a janela desenhar antes de abrir a impressão.
  window.setTimeout(() => janela.print(), 600);
}
